/**
 * Platform admin card: validate Stripe subscription IDs and clear stale ones
 * (POST /api/schools/platform/cleanup-stale-subscriptions), with a dry run.
 */
import React, { useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import {
  AdminCalloutBanner,
  AdminSectionCard,
  AdminStatusPill,
  ADMIN_COLORS,
} from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { confirmDialog, showDialog } from '../../utils/dialogs';

interface CleanupResult {
  total_checked: number;
  schools_cleaned: number;
  users_cleaned: number;
  errors: number;
  details: {
    entity_type: string;
    entity_id: number;
    entity_name: string;
    old_subscription_id: string;
    status: string;
    message: string;
  }[];
}

interface SubscriptionCleanupCardProps {
  /** Called after a real (non-dry-run) cleanup changed at least one school or user. */
  onCleaned: () => void;
}

export default function SubscriptionCleanupCard({ onCleaned }: SubscriptionCleanupCardProps) {
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);

  const handleSubscriptionCleanup = async (dryRun: boolean = true) => {
    try {
      setCleanupLoading(true);
      setCleanupResult(null);
      const result = await apiRequest<CleanupResult>({
        endpoint: `/schools/platform/cleanup-stale-subscriptions?dry_run=${dryRun}`,
        method: 'POST',
      });
      setCleanupResult(result);
      if (!dryRun && (result.schools_cleaned > 0 || result.users_cleaned > 0)) {
        // Refresh data after cleanup
        onCleaned();
      }
    } catch (error: any) {
      const message = error?.message || 'Failed to run subscription cleanup';
      showDialog(message);
    } finally {
      setCleanupLoading(false);
    }
  };

  return (
    <AdminSectionCard
      icon="cleaning-services"
      title="Subscription Cleanup"
      subtitle="Validate Stripe subscription IDs and clean up stale references from test environment migration."
    >
      <View className="flex-row" style={{ gap: 12 }}>
        <Button
          variant="outline"
          disabled={cleanupLoading}
          onPress={() => handleSubscriptionCleanup(true)}
          className="flex-row items-center justify-center gap-2 flex-1"
        >
          {cleanupLoading ? (
            <ActivityIndicator size="small" color={ADMIN_COLORS.text.primary} />
          ) : (
            <MaterialIcons name="search" size={18} color={ADMIN_COLORS.text.primary} />
          )}
          <Text className="text-sm font-semibold" style={{ color: ADMIN_COLORS.text.primary }}>
            Preview (Dry Run)
          </Text>
        </Button>
        <Button
          variant="destructive"
          disabled={cleanupLoading}
          onPress={async () => {
            const confirmed = await confirmDialog(
              'Confirm Cleanup',
              'This will clear invalid subscription IDs. Continue?',
              'Clean',
              'Cancel',
            );
            if (confirmed) await handleSubscriptionCleanup(false);
          }}
          className="flex-row items-center justify-center gap-2 flex-1"
        >
          <MaterialIcons name="cleaning-services" size={18} color="#ffffff" />
          <Text className="text-sm font-semibold text-white">Clean Stale IDs</Text>
        </Button>
      </View>

      {cleanupResult ? (
        <View
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            borderWidth: 1,
            borderColor: ADMIN_COLORS.borderSoft,
            borderRadius: 14,
            padding: 14,
            gap: 12,
          }}
        >
          <View className="flex-row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <Text className="text-sm font-semibold" style={{ color: ADMIN_COLORS.text.primary }}>
              Total checked: {cleanupResult.total_checked}
            </Text>
            <View className="flex-row" style={{ gap: 6 }}>
              <AdminStatusPill
                tone="success"
                label={`Valid ${cleanupResult.details.filter((d) => d.status === 'valid').length}`}
              />
              <AdminStatusPill
                tone="warning"
                label={`Stale ${cleanupResult.details.filter((d) => d.status === 'would_clean' || d.status === 'cleaned').length}`}
              />
              {cleanupResult.errors > 0 ? (
                <AdminStatusPill tone="error" label={`Errors ${cleanupResult.errors}`} />
              ) : null}
            </View>
          </View>
          {cleanupResult.details.filter((d) => d.status !== 'valid').length > 0 ? (
            <View style={{ gap: 6 }}>
              <Text
                className="text-xs font-semibold uppercase"
                style={{ color: ADMIN_COLORS.text.muted, letterSpacing: 0.5 }}
              >
                Stale subscriptions
              </Text>
              {cleanupResult.details
                .filter((d) => d.status !== 'valid')
                .map((item, idx) => (
                  <View
                    key={idx}
                    className="flex-row items-start"
                    style={{
                      gap: 10,
                      paddingVertical: 8,
                      borderTopWidth: idx === 0 ? 0 : 1,
                      borderTopColor: ADMIN_COLORS.borderSoft,
                    }}
                  >
                    <MaterialIcons
                      name={item.entity_type === 'school' ? 'school' : 'person'}
                      size={16}
                      color={
                        item.status === 'cleaned'
                          ? ADMIN_COLORS.success
                          : item.status === 'error'
                            ? ADMIN_COLORS.error
                            : ADMIN_COLORS.warning
                      }
                    />
                    <View style={{ flex: 1 }}>
                      <Text className="text-sm" style={{ color: ADMIN_COLORS.text.primary }}>
                        {item.entity_name}
                      </Text>
                      <Text className="text-xs" style={{ color: ADMIN_COLORS.text.secondary }}>
                        {item.message}
                      </Text>
                    </View>
                  </View>
                ))}
            </View>
          ) : null}
          {cleanupResult.schools_cleaned > 0 || cleanupResult.users_cleaned > 0 ? (
            <AdminCalloutBanner
              tone="success"
              body={`Cleaned ${cleanupResult.schools_cleaned} schools and ${cleanupResult.users_cleaned} users.`}
            />
          ) : null}
        </View>
      ) : null}
    </AdminSectionCard>
  );
}
